import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/logic/authProvider';
import { ProjectRepository } from '../../project/data/projectRepository';
import { useI18n } from '../../../shared/i18n/I18nContext';
import { useTheme } from '../../../shared/theme/ThemeContext';
import Routes from '../../../shared/routing/routes';
import './TasksCard.css';

const TaskSkeleton = () => (
  <div className="tasks-card__list" aria-hidden="true">
    {[0, 1, 2].map((item) => (
      <div className="tasks-card__item tasks-card__item--skeleton" key={item}>
        <div className="tasks-card-skeleton tasks-card-skeleton--title" />
        <div className="tasks-card-skeleton tasks-card-skeleton--line" />
      </div>
    ))}
  </div>
);

const TasksCard = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (!isExpanded || tasks.length > 0) return undefined;

    let cancelled = false;
    const loadTasks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const rows = await ProjectRepository.listTasks({ accessToken: user?.accessToken });
        if (!cancelled) setTasks(rows);
      } catch (err) {
        if (!cancelled) {
          setTasks([]);
          setError(err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [isExpanded, tasks.length, user?.accessToken]);

  const openTask = (task) => {
    navigate(Routes.taskTimesheets(task.id), { state: { task } });
  };

  return (
    <section className="tasks-card" style={{ borderColor: theme.border }} aria-busy={isLoading}>
      <div className="tasks-card__head">
        <div className="tasks-card__title-wrap">
          <h2 className="tasks-card__title">{t('tasks') || 'Tasks'}</h2>
          {isExpanded && (
            <p className="tasks-card__subtitle" style={{ color: theme.secondaryText }}>
              {t('tasksSubtitle') || 'The tasks assigned to you'}
            </p>
          )}
        </div>
        <div className="tasks-card__head-side">
          {!isLoading && tasks.length > 0 && (
            <span className="tasks-card__count" style={{ color: theme.secondaryText }}>
              {tasks.length}
            </span>
          )}
          <button
            type="button"
            className={`tasks-card__toggle${isExpanded ? ' tasks-card__toggle--expanded' : ''}`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse tasks' : 'Expand tasks'}
            aria-controls="tasks-card-content"
            onClick={() => setIsExpanded((current) => !current)}
          >
            <span aria-hidden="true">〱</span>
          </button>
        </div>
      </div>

      <div
        id="tasks-card-content"
        className={`tasks-card__content${isExpanded ? '' : ' tasks-card__content--collapsed'}`}
      >
        {isLoading ? (
          <TaskSkeleton />
        ) : error ? (
          <p className="tasks-card__status" style={{ color: theme.error }}>
            {error.message}
          </p>
        ) : tasks.length === 0 ? (
          <p className="tasks-card__status" style={{ color: theme.secondaryText }}>
            {t('noTasksAssignedYet') || 'No tasks assigned yet'}
          </p>
        ) : (
          <div className="tasks-card__list">
            {tasks.map((task) => (
              <button
                type="button"
                className="tasks-card__item"
                key={task.id}
                onClick={() => openTask(task)}
                style={{ borderColor: theme.border, color: theme.text }}
              >
                <span className="tasks-card__item-main">
                  <strong>{task.name || `#${task.id}`}</strong>
                  {task.projectName && (
                    <span style={{ color: theme.secondaryText }}>{task.projectName}</span>
                  )}
                </span>
                <span className="tasks-card__arrow" aria-hidden="true">
                  &rsaquo;
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TasksCard;
